import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ElementRef,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import { Subject, takeUntil } from 'rxjs';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  EIremboFormlyFieldTypes,
  ToastService,
} from '@irembo-andela/irembogov3-common';
import { generateNewFieldKeyFromSettingsLabel } from '../../../../core/utils/form-fields-formly-configurations/form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import { isFieldNew } from '../../utils/fb-new-field.util';

@Component({
  selector: 'irembogov-fb-field-wrapper',
  templateUrl: './fb-field-wrapper.component.html',
  styleUrls: ['./fb-field-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbFieldWrapperComponent implements OnInit, OnDestroy, OnChanges {
  @Input() field!: FormlyFieldConfig;
  @Input() isSelected = false;
  @Input() reviewMode = false;

  @Output() selectField = new EventEmitter<FormlyFieldConfig>();
  @Output() deleteField = new EventEmitter<FormlyFieldConfig>();
  @Output() duplicateField = new EventEmitter<FormlyFieldConfig>();

  showToolbar = false;
  keyCopied = false;

  // Formly context for rendering the field preview
  previewForm = new FormGroup({});
  previewModel: Record<string, any> = {};
  previewFields: FormlyFieldConfig[] = [];
  previewOptions: FormlyFormOptions = {};

  private destroy$ = new Subject<void>();

  constructor(
    private formBuilderService: FormBuilderService,
    private clipboard: Clipboard,
    private readonly cdr: ChangeDetectorRef,
    private readonly el: ElementRef<HTMLElement>,
    private readonly toastService: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['field'] && this.field) {
      this.buildPreviewFields();
    }
  }

  ngOnInit(): void {
    if (this.field) {
      this.buildPreviewFields();
    }

    this.formBuilderService.configureFieldAction$
      .pipe(takeUntil(this.destroy$))
      .subscribe((configuredField: FormlyFieldConfig | null) => {
        const selected = configuredField?.key === this.field?.key;
        if (this.isSelected !== selected) {
          this.isSelected = selected;
          this.cdr.markForCheck();
          // When a field becomes selected (e.g. jumped-to from the outline), bring it
          // into view. 'nearest' avoids jarring scrolls when it is already visible and
          // works for hidden/display-rule fields too, since they still render a wrapper.
          if (selected) {
            setTimeout(
              () =>
                this.el.nativeElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                }),
              0
            );
          }
        }
      });

    // Field config is mutated in place (e.g. toggling "Required"), so the
    // OnPush @Input reference doesn't change. Re-check on every form refresh so
    // header bindings (label, required asterisk, key) stay in sync live.
    this.formBuilderService.formlyFieldsSubject$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data.forceReload && this.field) {
          this.buildPreviewFields();
        }
        this.cdr.markForCheck();
      });
  }

  get isHidden(): boolean {
    // Field is hidden if hide is explicitly true or if there's a hide expression
    // (in builder mode, expression-hidden fields show as "Hidden Field" placeholders)
    return !!this.field?.hide || this.hasHideCondition;
  }

  // A `hide` expression of constant `false` hides nothing, so it must not count
  // as a condition. Location widgets saved before the generator stopped
  // emitting `hide: 'false'` still carry it, and would otherwise render as
  // "Hidden Field" placeholders on the canvas.
  private get hasHideCondition(): boolean {
    const hide = this.field?.expressions?.['hide'];
    if (typeof hide === 'string') {
      const condition = hide.trim();
      return condition.length > 0 && condition !== 'false';
    }
    return !!hide;
  }

  get isNonDisplay(): boolean {
    return !!this.field?.props?.['hideField'];
  }

  get isVisible(): boolean {
    return !this.isHidden && !this.isNonDisplay;
  }

  get fieldLabel(): string {
    return (
      this.field?.props?.label || this.field?.props?.['label'] || 'Untitled'
    );
  }

  /** The actual label (no "Untitled" fallback) — what the inline editor edits. */
  get rawLabel(): string {
    const label = this.field?.props?.['label'];
    return typeof label === 'string' ? label : '';
  }

  /**
   * Inline label rename (double-click, same as blocks/sections). Applies the new
   * label, refreshes the form, and warns if it duplicates another field's label —
   * the backend enforces label uniqueness form-wide on save.
   */
  onLabelChange(newLabel: string): void {
    const label = (newLabel || '').trim();
    if (!label || label === this.rawLabel) return;
    // Reject duplicates BEFORE applying — the backend enforces unique labels
    // form-wide, so don't keep an edit that would fail on save. The inline editor
    // reverts to the current label because we leave field.props.label unchanged.
    if (this.formBuilderService.isDuplicateFieldLabel(label, this.fieldKey)) {
      this.toastService.show({
        body: `Another field already uses the label "${label}". Labels must be unique — the change was not applied.`,
        type: 'warning',
      });
      this.cdr.markForCheck();
      return;
    }
    if (!this.field.props) this.field.props = {};
    this.field.props['label'] = label;
    if (isFieldNew(this.field)) {
      generateNewFieldKeyFromSettingsLabel(this.field, this.formBuilderService);
    }
    // Persist + refresh without clearing the selection or forcing a full reload.
    this.formBuilderService.refreshFormlyForm(false, false);
    this.cdr.markForCheck();
  }

  get fieldKey(): string {
    return this.field?.key?.toString() || '';
  }

  get fieldType(): string {
    return this.field?.type?.toString() || '';
  }

  get isRequired(): boolean {
    return !!this.field?.props?.required;
  }

  get isLocationWidgetRoot(): boolean {
    return !!this.field?.props?.['locationWidgetRoot'];
  }

  get tooltipText(): string {
    return this.field?.props?.['tooltip'] || '';
  }

  get isNonConfigurable(): boolean {
    const isLocationWidgetField = Boolean(
      this.field?.props?.['locationWidgetKey'] ||
        this.field?.props?.['locationWidgetRoot'] ||
        this.field?.props?.['countryFieldKey']
    );
    return !!this.field?.props?.['nonConfigurable'] && !isLocationWidgetField;
  }

  get internalDescription(): string {
    return this.field?.props?.['internalDescription'] || '';
  }

  onSelect(event: MouseEvent): void {
    event.stopPropagation();
    this.selectField.emit(this.field);
  }

  copyKey(): void {
    this.clipboard.copy(this.fieldKey);
    this.keyCopied = true;
    setTimeout(() => {
      this.keyCopied = false;
      this.cdr.markForCheck();
    }, 2000);
  }

  duplicate(): void {
    this.duplicateField.emit(this.field);
  }

  configure(): void {
    this.selectField.emit(this.field);
  }

  configureLocation(): void {
    const localLocationFieldKey = this.field.props?.['localLocationFieldKey'];
    const localLocationField = this.field.parent?.fieldGroup?.find(
      field => field.key === localLocationFieldKey
    );
    const sourceField = localLocationField ?? this.field;
    const locationEditorField: FormlyFieldConfig = {
      ...sourceField,
      key: this.field.key,
      id: this.field.id,
      type: EIremboFormlyFieldTypes.customcascadingdropdowns,
      props: {
        ...sourceField.props,
        ...this.field.props,
        configs: sourceField.props?.['configs'] ?? [],
      },
    };
    if (this.field.parent) {
      Object.defineProperty(locationEditorField, 'parent', {
        value: this.field.parent,
        enumerable: false,
        writable: true,
        configurable: true,
      });
    }
    this.selectField.emit(locationEditorField);
  }

  delete(): void {
    this.deleteField.emit(this.field);
  }

  onMouseEnter(): void {
    if (!this.reviewMode) {
      this.showToolbar = true;
      this.cdr.markForCheck();
    }
  }

  onMouseLeave(): void {
    this.showToolbar = false;
    this.cdr.markForCheck();
  }

  private buildPreviewFields(): void {
    const previewProps = { ...this.field.props };
    // The builder canvas is a visual editor, not the runtime form. Mounting a
    // remote dataset here makes UI Common eagerly fetch every non-paginated
    // item (for example all VILLAGE entries) once per rendered field.
    delete previewProps['dataset'];

    // Create a shallow copy of the field config for formly to render.
    // We strip wrappers to avoid the V1 field-wrapper (which adds its own label/toolbar)
    // since we provide our own header with label + toolbar above.
    const previewField: FormlyFieldConfig = {
      ...this.field,
      // Remove wrappers to prevent V1 field-wrapper from rendering duplicate label/toolbar
      wrappers: [],
      // Keep hide false so formly actually renders the component
      hide: false,
      // Pass through props to configure formState for hiding builder chrome
      props: {
        ...previewProps,
        hideHeader: true,
      },
    };
    this.previewFields = [previewField];
    this.previewForm = new FormGroup({});
    this.previewModel = {};
    this.previewOptions = {
      formState: {
        hideBuilderWrapper: true,
      },
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
